import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Vehicle, VehicleDisplayed } from "./telemetryApi";
import {
  analyticVehiclesListOfACompanyAsync,
  selectAnalyticVehiclesListOfACompany,
  selectAnalyticVehiclesListOfACompanyStatus,
} from "./telemetrySlice";
import "../../i18n/i18n";
import { useTranslation } from "react-i18next";
import { isOlderThanOneWeek } from "../lib";

export const useVehicles = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const [companySelected, setCompanySelected] = useState<string>("");
  const analyticVehiclesListOfACompany = useAppSelector(
    selectAnalyticVehiclesListOfACompany,
  );
  const analyticVehiclesListOfACompanyStatus = useAppSelector(
    selectAnalyticVehiclesListOfACompanyStatus,
  );

  const findKeysToTranslate = useCallback(
    (key: keyof VehicleDisplayed, value: string) => {
      switch (key) {
        case "connectionStatus":
          return t(
            `vehiclesOfACompany.column.connectionStatus.${value ?? "null"}`,
          );
        case "isEligible":
          return t(`vehiclesOfACompany.column.isEligible.${value ?? "null"}`);
        case "geolocationError": {
          if (!value) return "";

          const parsedError = JSON.parse(value) as {
            failure?: {
              reason?: string;
            };
          };

          if (parsedError.failure && parsedError.failure.reason) {
            return t(
              `vehiclesOfACompany.column.geolocationError.${parsedError.failure.reason}`,
            );
          }
          return value;
        }
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(() => {
    return [
      "connectionStatus",
      "isEligible",
      "geolocationError",
    ] as (keyof VehicleDisplayed)[];
  }, []);

  const vehiclesListWithId = useMemo(() => {
    if (analyticVehiclesListOfACompanyStatus === "success") {
      const list = analyticVehiclesListOfACompany.map((v) => {
        const hasStaleConnectionRequest =
          v.connectionStatus === "PENDING" &&
          v.requestedConnectionAt !== null &&
          isOlderThanOneWeek(v.requestedConnectionAt);
        let newDict = {
          ...v,
          id: (v as unknown as Vehicle).uuid,
          department: v.department ?? null,
          deviceId: v.deviceId ?? null,
          statusSeverity: hasStaleConnectionRequest
            ? ("error" as const)
            : undefined,
        };

        Object.entries(v).forEach(([key, value]) => {
          if (keysToTranslate.includes(key as keyof VehicleDisplayed)) {
            newDict = {
              ...newDict,
              [key]: findKeysToTranslate(
                key as keyof VehicleDisplayed,
                value as string,
              ),
            };
          }
        });
        return newDict;
      });

      return list;
    } else {
      return [];
    }
  }, [
    analyticVehiclesListOfACompany,
    analyticVehiclesListOfACompanyStatus,
    findKeysToTranslate,
    keysToTranslate,
  ]);

  useEffect(() => {
    if (
      (analyticVehiclesListOfACompanyStatus === "idle" ||
        params.id !== companySelected) &&
      params.id
    ) {
      void dispatch(
        analyticVehiclesListOfACompanyAsync({ companyUuid: params.id }),
      );
      setCompanySelected(params.id);
    }
  }, [
    analyticVehiclesListOfACompanyStatus,
    companySelected,
    params.id,
    dispatch,
  ]);

  return vehiclesListWithId;
};
